import { motion } from 'framer-motion';
import { useContext, useState, useEffect } from 'react';
import { ProductsContext } from '../../context/products.context';
import { CipsElement } from '../../components/CipsElement';
import { SchoolEnrolledTable } from '../../components/SchoolEnrolledTable';
import { JobsTable } from '../../components/JobsTable';
import CipCatalog from '../../components/CipCatalog';
import CipEquity from '../../components/CipEquity';
import CipEnrollmentsOthers from '../../components/CipEnrollmentsOthers';
import CipEnrollmentsColleges from '../../components/CipEnrollmentsColleges';
function Cip() {
  const {
    renton,
    schoolDemo,
    districtName,
    workforce,
    localColleges,
    cipEquityData,
    cipDoc,
    currentCip,
    jobs,
    collegeData,
    userDefaults,
    offeredCip,
    workforceMenu,
    nontrad
  } = useContext(ProductsContext);

  const [showAllTotals, setShowAllTotals] = useState(false);
  const agriculture = (workforceMenu &&
    workforceMenu.assignProgramClusterCips
      .map(e => e[currentCip])
      .filter(Boolean)
  );

  const [isDataReady, setIsDataReady] = useState(false);

  useEffect(() => {
    if (cipDoc && renton && workforce && jobs) {
      const timer = setTimeout(() => {
        setIsDataReady(true);
      }, 30); // 30ms delay for data readiness
      return () => clearTimeout(timer);
    }
  }, [cipDoc, renton, workforce, jobs]);

  function findCommonCipsAreas(arr1, arr2) {
    if (!showAllTotals) {
      return arr1.some(item => arr2.flat().includes(item) && offeredCip.includes(item));
    } else {
      return arr1.some(item => arr2.flat().includes(item));
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [currentCip]);

  // Fade in only. An exit animation (AnimatePresence mode="wait") could get stuck
  // when the CIP changed or data arrived mid-exit, leaving the page blank.
  const fadeVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1, 
      transition: { duration: 0.4, ease: "easeInOut" } 
    }
  };

  return (
    <div className='bg-zinc-50 min-h-screen w-full mx-auto px-2'>

     {/* CIP Info Header */}
<div className="relative left-0 w-full z-30 mt-[60px] bg-white rounded-t-lg">
  <div className="max-w-7xl mx-auto py-6 flex items-center justify-between">
    <div className="relative flex flex-col justify-start h-full overflow-hidden">
      {isDataReady && (
          <motion.div
            key={currentCip} // remount + fade in on CIP change
            variants={fadeVariants}
            initial="initial"
            animate="animate"
          >
            <div className="flex flex-row items-start mt-4">

              <span className="text-2xl font-light px-5">
                {currentCip.toString().replace(/\B(?=(\d{4})+(?!\d))/g, '.')}
              </span>

              {/* CIP title – remove the mt-7 so it lines up at the top */}
              <div className="text-2xl font-bold leading-7 text-slate-900">
                {cipDoc && cipDoc.cipDetails 
                  ? cipDoc.cipDetails.cipName 
                  : 'Totals'}

              {/* Nontrad label */}
              <span className="text-xl px-2 font-light">
                {nontrad.female.includes(currentCip) && ' (Non Traditional - Female)'}
                {nontrad.male.includes(currentCip) && ' (Non Traditional - Male)'}
              </span>
            </div></div>
          </motion.div>
      )}
    </div>
  </div>
</div>

      <div className='flex max-w-[1400px]'>
        <div className='flex-col max-w-[1200px] flex-grow mx-auto'>
          {isDataReady && (
              <motion.div
                key={currentCip}
                variants={fadeVariants}
                initial="initial"
                animate="animate"
              >
                {/* CIP Catalog */}
                {cipDoc && <CipCatalog cip={currentCip} cipCatalog={cipDoc} />}

                {/* School Enrollment Table */}
                {renton && (
                  <SchoolEnrolledTable
                    cipcodeName={
                      cipDoc && cipDoc.cipDetails
                        ? cipDoc.cipDetails.cipName
                        : currentCip + " Totals"
                    }
                    school={renton.find((e) => e.cipcode === currentCip)}
                    districtName={districtName}
                  />
                )}

                {/* Jobs Data Section */}
                {jobs && jobs.socData && (
                  <div className='bg-white p-4 rounded-t-lg mt-11'>
                    <h3 className='px-3 pt-2 text-2xl font-bold leading-7 text-slate-900'>
                      {workforce}
                    </h3>
                    <h5 className='px-3 font-medium text-xl mt-0 mb-2 text-blue-600'>
                      Labor Market Data for {cipDoc && cipDoc.cipDetails ? cipDoc.cipDetails.cipName : currentCip + " Totals"}
                    </h5>

                    {currentCip.length !== 6 && (
                      !showAllTotals ? (
                        <div className='flex flex-col items-start text-gray-700'>
                          <span className='text-xl'>Displaying {districtName} {currentCip} Jobs Data</span>
                          <button
                            className='text-md pl-5 p-2 text-blue-500 italic hover:underline print:hidden'
                            onClick={() => setShowAllTotals(true)}
                          >
                            show all {currentCip} jobs data...
                          </button>
                        </div>
                      ) : (
                        <div className='flex flex-col items-start text-gray-700'>
                          <span className='text-xl'>Displaying All {currentCip} Jobs Data</span>
                          <button
                            className='text-md pl-5 p-2 text-blue-500 italic hover:underline print:hidden'
                            onClick={() => setShowAllTotals(false)}
                          >
                            show only {districtName} {currentCip} jobs data ...
                          </button>
                        </div>
                      )
                    )}

                    <CipsElement
                      items={jobs.socData && (
                        currentCip.length === 6
                          ? jobs.socData
                              .map((e) => (Array.isArray(e.alignedCip) && e.alignedCip.includes(currentCip) && e))
                              .filter(Boolean)
                          : jobs.socData
                              .map((e) => (Array.isArray(e.alignedCip) && findCommonCipsAreas(e.alignedCip, agriculture) && e))
                              .filter(Boolean)
                      )}
                      resourceName="jobs"
                      itemComponent={JobsTable}
                    />
                  </div>
                )}

                {/* Equity Data Section */}
                {cipEquityData && cipEquityData.length > 0 && schoolDemo && (
                  <CipEquity 
                    cip={currentCip}
                    cipEquityData={cipEquityData}
                    schoolDemographic={schoolDemo}
                  />
                )}

                {/* Other District Enrollments */}
                <CipEnrollmentsOthers
                  cip={currentCip}
                  cipCatalog={cipDoc}
                  defaultConsortium={userDefaults ? userDefaults.defaultConsortium : null}
                />
                {/* Colleges Enrollments */}
                <CipEnrollmentsColleges
                  cip={currentCip}
                  collegeData={collegeData}
                  localColleges={localColleges}
                />
              </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Cip;